import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { ZApiGuideTrigger } from "@/components/ZApiGuide";
import SaiposCard from "@/components/integrations/SaiposCard";
import { useSector } from "@/lib/sectorContext";
import {
  WhatsappLogo, CheckCircle, CircleDashed, CreditCard, ArrowSquareOut,
  CaretRight, Copy, Check, ArrowsClockwise, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { CUSTOMER_BILLING_ENABLED } from "@/lib/features";

/* ── v0.99.42 · Fev/2026 · ERP catalog sector-aware ────────────────
 * Cada ERP declara os setores em que faz sentido aparecer. Se o
 * tenant é de "restaurant" só vê Saipos; se é "automotive" NÃO vê
 * Saipos (que é ERP de food service). Extensível: basta acrescentar
 * novos ERPs com sua lista de setores compatíveis.
 *   allowedSectors: [...códigos]  → whitelist
 *   allowedSectors: "*"          → aparece em todos (raro; ex.: ERP fiscal)
 * O código do setor é o `SectorProfile.code` retornado por
 * /api/sector/preview/{code}.
 */
const ERP_CATALOG = [
  {
    key: "saipos",
    Component: SaiposCard,
    allowedSectors: ["restaurant"],
  },
  // Futuros ERPs — exemplo pronto pra plugar:
  //   { key: "totvs_salon", Component: TotvsSalonCard,
  //     allowedSectors: ["salon", "beauty"] },
  //   { key: "autocom",     Component: AutocomCard,
  //     allowedSectors: ["automotive", "automotive_dealer",
  //                      "automotive_carwash"] },
  //   { key: "tinyerp",     Component: TinyErpCard, allowedSectors: "*" },
];

/**
 * Integrations page — focused on the two integrations that actually matter
 * for the operational SaaS:
 *   1. WhatsApp (via Z-API) — required to receive and send messages
 *   2. Stripe — required to receive payments / send payment links
 *
 * Each card includes an inline step-by-step guide so the operator (oficina /
 * concessionária) can self-serve without external support.
 */

const BACKEND_URL = (typeof window !== "undefined" && window.location.hostname === "agentus-frontend-production.up.railway.app")
  ? "https://agentos-production-1824.up.railway.app"
  : (process.env.REACT_APP_BACKEND_URL || "");

const WEBHOOK_URL = `${BACKEND_URL}/api/webhooks/zapi`;

function CopyButton({ value, label = "Copiar" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      toast.success("Copiado!");
      setTimeout(() => setDone(false), 1800);
    } catch {
      toast.error("Falha ao copiar.");
    }
  };
  return (
    <button
      onClick={copy}
      type="button"
      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-600 hover:text-slate-900 transition-colors"
      data-testid="integrations-copy-btn"
    >
      {done ? <Check size={12} weight="bold" /> : <Copy size={12} weight="regular" />}
      {done ? "Copiado" : label}
    </button>
  );
}

function StatusPill({ status }) {
  // Three visual states matching the topbar pill:
  //   connected → green CONECTADO
  //   pairing   → amber PAREANDO (credenciais salvas mas QR não escaneado / celular offline)
  //   *         → slate AGUARDANDO CONFIGURAÇÃO
  if (status === "connected") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700" data-testid="zapi-status-pill">
        <CheckCircle size={11} weight="fill" /> Conectado
      </span>
    );
  }
  if (status === "pairing") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700" data-testid="zapi-status-pill">
        <CircleDashed size={11} weight="regular" className="animate-spin" /> Aguardando QR Code
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-500" data-testid="zapi-status-pill">
      <CircleDashed size={11} weight="regular" /> Aguardando configuração
    </span>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-semibold flex items-center justify-center">
        {n}
      </span>
      <div className="flex-1 min-w-0 pb-3">
        <div className="text-[13px] font-medium text-slate-900">{title}</div>
        <div className="text-[12.5px] text-slate-600 mt-0.5 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function fmtBRPhone(digits) {
  // 5511999998888 → +55 11 99999-8888
  const d = String(digits || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 13 && d.startsWith("55")) {
    return `+55 ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9)}`;
  }
  if (d.length === 12 && d.startsWith("55")) {
    return `+55 ${d.slice(2, 4)} ${d.slice(4, 8)}-${d.slice(8)}`;
  }
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `+${d}`;
}

// ─── WEBHOOK URL CARD (auto-onboarding por tenant) ──────────────────────
//
// Substitui a antiga URL global genérica. Cada tenant recebe:
//   • Uma URL exclusiva (`/api/webhooks/zapi/{tenant_id}?token={secret}`)
//   • Um secret independente — se um cliente vazar, só ele é afetado
//   • Botão "Regenerar token" pra rotacionar em 1 clique
//
// O secret nunca é mostrado em texto puro; só o preview (`6 chars…4 chars`)
// dentro da URL. O botão de copiar entrega a URL COMPLETA no clipboard.
function WebhookUrlCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/zapi/webhook-config");
      setCfg(data);
    } catch (err) {
      toast.error("Não foi possível carregar a URL do webhook.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copy = async () => {
    if (!cfg?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(cfg.webhook_url);
      setCopied(true);
      toast.success("URL copiada!", {
        description: "Cole no campo 'Ao receber' do painel Z-API.",
      });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Falha ao copiar.");
    }
  };

  const rotate = async () => {
    if (!window.confirm(
      "Regenerar o token do webhook?\n\n" +
      "⚠️ A URL atual vai parar de funcionar imediatamente.\n" +
      "Você PRECISARÁ atualizar a URL no painel Z-API depois.\n\n" +
      "Continuar?"
    )) return;
    setRotating(true);
    try {
      await api.post("/integrations/zapi/webhook-config/regenerate");
      toast.success("Token regenerado", {
        description: "Copie a nova URL e cole no painel Z-API.",
      });
      await load();
    } catch (err) {
      toast.error("Falha ao regenerar.");
    } finally {
      setRotating(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="mt-2 flex items-center gap-2 bg-white border border-slate-200 rounded px-2.5 py-2 text-[11.5px] text-slate-500">
        <CircleDashed size={12} weight="regular" className="animate-spin" />
        Gerando URL personalizada…
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1.5" data-testid="zapi-webhook-url-card">
      <div className="flex items-stretch gap-2 bg-white border border-slate-200 rounded overflow-hidden">
        <code
          className="text-[11px] font-mono text-slate-700 flex-1 min-w-0 px-2.5 py-2 overflow-x-auto whitespace-nowrap"
          data-testid="zapi-webhook-url"
        >
          {cfg.webhook_url}
        </code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 border-l border-slate-200 bg-slate-50 hover:bg-slate-100 text-[11px] font-medium text-slate-700 transition-colors"
          data-testid="zapi-webhook-copy"
        >
          {copied ? <Check size={12} weight="bold" className="text-emerald-600" /> : <Copy size={12} weight="regular" />}
          {copied ? "Copiado" : "Copiar URL"}
        </button>
      </div>
      <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck size={11} weight="fill" className="text-emerald-600" />
          URL exclusiva desta conta · token privado
        </span>
        <button
          type="button"
          onClick={rotate}
          disabled={rotating}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 disabled:opacity-40 transition-colors"
          data-testid="zapi-webhook-rotate"
        >
          <ArrowsClockwise size={11} weight="regular" className={rotating ? "animate-spin" : ""} />
          {rotating ? "Regenerando…" : "Regenerar token"}
        </button>
      </div>
    </div>
  );
}


// ─── Z-API CARD ──────────────────────────────────────────────────────────────

function ZApiCard({ row, onSaved }) {
  const [showSteps, setShowSteps] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(row?.config || {});
  const [saving, setSaving] = useState(false);
  // Live status from the Z-API /status probe — single source of truth.
  // The stored `row.status` is only used as fallback while loading.
  const [liveStatus, setLiveStatus] = useState(null);
  const [probing, setProbing] = useState(false);

  useEffect(() => { setForm(row?.config || {}); }, [row]);

  const probeLive = async () => {
    setProbing(true);
    try {
      // Hard client-side timeout so the button never hangs forever even if
      // the Railway worker is stuck. 25s gives the backend's 20s read
      // timeout some slack.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25_000);
      const { data } = await api.get("/integrations/zapi/status", {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      setLiveStatus(data);
    } catch (err) {
      const isAbort = err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
      setLiveStatus({
        connected: false,
        configured: true,
        error: isAbort ? "client_timeout" : (err?.response?.data?.error || "probe_failed"),
      });
    } finally {
      setProbing(false);
    }
  };

  // Auto-probe when card mounts and whenever the row changes (e.g. after save).
  // Also re-probe every 30s while the user is on this page so transient
  // QR-code disconnections become visible without a manual refresh.
  useEffect(() => {
    probeLive();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") probeLive();
    }, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id]);

  const save = async (e) => {
    e.preventDefault();

    // ── Client-side sanity checks (mirror the backend rules) ──────────
    // These catch the two most common onboarding mistakes BEFORE we hit
    // the backend, so the operator sees a precise message inline instead
    // of a generic "Falha ao salvar".
    const url = (form.instance_url || "").trim();
    const instToken = (form.instance_token || "").trim();
    const clientToken = (form.client_token || "").trim();

    if (!url || !instToken) {
      toast.error("Preencha 'API da instância' e 'Token da instância'.");
      return;
    }
    const urlMatch = url.match(/^https?:\/\/[^/]+\/instances\/([A-Za-z0-9]+)\/token\/([A-Za-z0-9]+)/);
    if (!urlMatch) {
      toast.error("API da instância inválida. Cole no formato https://api.z-api.io/instances/SEU_ID/token/SEU_TOKEN.");
      return;
    }
    const [, urlInstanceId, urlToken] = urlMatch;
    if (urlToken !== instToken) {
      toast.error("O 'Token da instância' não bate com o token dentro da 'API da instância'. Confira se você não cortou nenhum caractere.");
      return;
    }
    if (clientToken && clientToken === urlInstanceId) {
      toast.error("O 'Token de segurança da conta' está com o valor do ID da instância. Eles são valores diferentes — abra Conta → Segurança no painel Z-API e copie o token correto.", { duration: 8000 });
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.put(`/integrations/${row.id}`, { config: form, status: "connected" });
      const detected = data?.config?.connected_phone;
      if (detected) {
        toast.success(`Credenciais salvas · verificando sessão WhatsApp`, {
          description: `Detectamos o número ${fmtBRPhone(detected)}. Estamos confirmando se o QR Code já foi escaneado…`,
        });
      } else {
        toast.success("Credenciais salvas!", {
          description: "Agora vou verificar se o WhatsApp está pareado. Pode demorar alguns segundos.",
        });
      }
      setEditing(false);
      // Probe live + refresh row in parent
      await probeLive();
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  // ── derive the EFFECTIVE status from the live probe ──────────────────
  // Priority: live probe result > stored row.status (fallback during first
  // render before probe completes).
  const liveConnected = liveStatus?.connected === true;
  const liveConfigured = liveStatus?.configured === true;
  const hasCreds = Boolean(row?.config?.instance_url && row?.config?.instance_token);

  let effectiveStatus = "disconnected";
  if (liveStatus === null) {
    // First paint, before probe completes — show whatever the DB has.
    effectiveStatus = row?.status || "disconnected";
  } else if (liveConnected) {
    effectiveStatus = "connected";
  } else if (liveConfigured || hasCreds) {
    // Credentials are saved but session is NOT paired (QR not scanned / phone offline)
    effectiveStatus = "pairing";
  } else {
    effectiveStatus = "disconnected";
  }

  const connected = effectiveStatus === "connected";
  const detectedPhone = liveStatus?.connected_phone || row?.config?.connected_phone;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="integration-card-z_api">
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <WhatsappLogo size={22} weight="fill" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-[15px] font-semibold text-slate-900 tracking-tight">WhatsApp</h3>
                <span className="text-[10px] text-slate-400 font-mono">via Z-API</span>
              </div>
              <p className="text-[12.5px] text-slate-500 mt-0.5 leading-relaxed">
                Conecta seu número de WhatsApp ao AgentUS. Mensagens recebidas viram conversas automáticas.
              </p>
            </div>
          </div>
          <StatusPill status={effectiveStatus} />
        </div>

        {effectiveStatus === "pairing" && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="zapi-pairing-banner">
            <div className="flex items-start gap-2">
              <CircleDashed size={14} weight="regular" className="text-amber-700 mt-0.5 animate-spin shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-medium text-amber-900">
                  {liveStatus?.error === "client_timeout"
                    ? "Verificação demorou demais — a Z-API não respondeu a tempo"
                    : liveStatus?.error === "client_token_missing"
                    ? "Token de segurança da conta obrigatório"
                    : liveStatus?.error === "client_token_invalid"
                    ? "Token de segurança da conta rejeitado pela Z-API"
                    : liveStatus?.error === "subscription_inactive"
                    ? "Assinatura da instância Z-API está inativa"
                    : liveStatus?.error === "credentials_rejected"
                    ? "Credenciais rejeitadas pela Z-API"
                    : liveStatus?.error?.startsWith("zapi_http_4")
                    ? "Credenciais foram rejeitadas pela Z-API"
                    : liveStatus?.error?.startsWith("zapi_http_5")
                    ? "Z-API está fora do ar no momento"
                    : "Credenciais salvas — falta parear o WhatsApp"}
                </div>
                <p className="text-[12px] text-amber-800 mt-0.5 leading-relaxed">
                  {liveStatus?.error === "client_timeout" ? (
                    <>Tente novamente em alguns segundos. Se persistir, sua instância Z-API pode estar com o celular offline ou em modo "Pausado".</>
                  ) : liveStatus?.error === "client_token_missing" ? (
                    <>Sua conta Z-API exige o <b>Token de segurança da conta</b>. Abra <a href="https://app.z-api.io" target="_blank" rel="noopener noreferrer" className="underline font-medium">app.z-api.io</a> → topo direito clique no seu nome → <b>Minha conta</b> (ou <b>Segurança</b> no menu lateral) → seção <b>"3. Token de segurança da conta"</b>. Cole esse valor no campo correspondente acima.</>
                  ) : liveStatus?.error === "client_token_invalid" ? (
                    <>O <b>Token de segurança da conta</b> que você colou não é aceito pela Z-API. Confira que NÃO é o ID da instância: o valor correto está em <a href="https://app.z-api.io" target="_blank" rel="noopener noreferrer" className="underline font-medium">app.z-api.io</a> → <b>Minha conta → Segurança → "3. Token de segurança da conta"</b>.</>
                  ) : liveStatus?.error === "subscription_inactive" ? (
                    <>Sua instância Z-API está com assinatura vencida ou cancelada. Acesse o painel Z-API e regularize antes de tentar novamente.</>
                  ) : liveStatus?.error === "credentials_rejected" ? (
                    <>Confira se a <strong>API da instância</strong>, o <strong>Token da instância</strong> e o <strong>Token de segurança da conta</strong> são todos da MESMA conta e instância no painel Z-API.</>
                  ) : liveStatus?.error?.startsWith("zapi_http_4") ? (
                    <>Confira se a <strong>API da instância</strong>, o <strong>Token da instância</strong> e o <strong>Token de segurança da conta</strong> estão corretos e correspondem à mesma instância no painel Z-API.</>
                  ) : liveStatus?.error?.startsWith("zapi_http_5") ? (
                    <>Aguarde alguns minutos e clique em "Verificar agora" novamente. Sua conexão será detectada assim que o serviço voltar.</>
                  ) : (
                    <>Acesse o painel da Z-API, escaneie o <strong>QR Code</strong> com o WhatsApp do seu celular e mantenha o celular conectado à internet. Em poucos segundos esse selo vira <strong>verde</strong>.</>
                  )}
                </p>
                {liveStatus?.zapi_error && (
                  <p className="text-[11px] text-amber-700 mt-1 font-mono">
                    Resposta Z-API: {liveStatus.zapi_error}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-3">
                  <button
                    onClick={probeLive}
                    disabled={probing}
                    data-testid="zapi-recheck-btn"
                    className="inline-flex items-center gap-1.5 text-[12px] text-amber-900 hover:text-amber-950 font-medium underline underline-offset-2 disabled:opacity-50"
                  >
                    {probing ? "Verificando…" : "Verificar agora"}
                  </button>
                  {liveStatus?.status_code && (
                    <span className="text-[11px] text-amber-700 font-mono">
                      HTTP {liveStatus.status_code}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {connected && detectedPhone && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full" data-testid="zapi-connected-phone">
            <WhatsappLogo size={12} weight="fill" className="text-emerald-700" />
            <span className="text-[12px] text-emerald-800 font-medium tabular-nums tracking-tight">
              {fmtBRPhone(detectedPhone)}
            </span>
            <span className="text-[10px] text-emerald-700 uppercase tracking-[0.14em] font-medium">conectado</span>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowSteps((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-700 hover:text-slate-900 font-medium transition-colors"
            data-testid="integration-z_api-steps-toggle"
          >
            <CaretRight size={11} weight="bold" className={`transition-transform ${showSteps ? "rotate-90" : ""}`} />
            Como conectar (passo a passo)
          </button>
          <ZApiGuideTrigger label="Ver guia visual" className="ml-1" />
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-3 py-1.5 text-[12px] font-medium hover:bg-slate-800 transition-colors"
              data-testid="integration-z_api-configure"
            >
              {connected ? "Editar credenciais" : "Configurar agora"}
            </button>
          )}
        </div>

        {showSteps && (
          <div className="mt-5 bg-slate-50 border border-slate-200 rounded-md p-4 space-y-0.5" data-testid="integration-z_api-steps">
            <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-slate-200">
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium text-slate-900">Prefere ver com prints anotados?</div>
                <div className="text-[11.5px] text-slate-500">5 telas reais do painel Z-API com setas e numeração mostrando onde clicar.</div>
              </div>
              <ZApiGuideTrigger label="Abrir guia visual" />
            </div>
            <Step n="1" title="Crie uma conta no Z-API">
              Acesse <a href="https://app.z-api.io" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline underline-offset-2 hover:text-slate-700 inline-flex items-center gap-1">app.z-api.io <ArrowSquareOut size={10} /></a> e crie sua conta. Eles oferecem plano grátis pra testar.
            </Step>
            <Step n="2" title="Crie uma instância">
              No painel Z-API, clique em <b>"+ Criar Instância"</b>. Vai aparecer 2 dados: <b>API da instância</b> (URL completa) e <b>Token da instância</b>. Vamos copiar os dois no passo 6.
            </Step>
            <Step n="3" title="Pegue o Token de segurança da conta">
              Esse fica em outro lugar — <b>NÃO é</b> o ID da instância. Caminho:<br/>
              <span className="block mt-1.5">• Topo direito → clique no <b>seu nome</b> → <b>Minha conta</b></span>
              <span className="block">• Ou no menu lateral esquerdo: <b>Segurança</b></span>
              <span className="block">• Role até a seção <b>"3. Token de segurança da conta"</b></span>
              <span className="block">• Copie esse valor (uma string aleatória, diferente do ID da instância)</span>
              <span className="block mt-1 text-slate-500">Se a sua conta Z-API não usa esse recurso de segurança, pode deixar em branco aqui.</span>
            </Step>
            <Step n="4" title="Configure o webhook no painel Z-API">
              Volte na sua instância → <b>"Webhooks e configurações gerais"</b> → campo <b>"Ao receber"</b>. Cole esta URL <b>exclusiva da sua conta</b>:
              <WebhookUrlCard />
              <span className="text-[11.5px] text-slate-500 mt-2 block leading-relaxed">
                <b>⚠️ Não compartilhe</b> esta URL — o token nela é privado da sua conta AgentUS. Deixe os outros toggles do webhook desligados (especialmente "Notificar enviadas por mim").
              </span>
            </Step>
            <Step n="5" title="Conecte seu WhatsApp (QR Code)">
              No painel Z-API, clique em <b>"Conectar"</b> ou <b>"Show QR Code"</b>. No celular do seu negócio: WhatsApp → Configurações → Aparelhos conectados → Conectar um aparelho → escaneie o QR.
            </Step>
            <Step n="6" title="Cole as credenciais aqui no AgentUS">
              Clique em <b>"Configurar agora"</b> acima. Cole <b>API da instância</b>, <b>Token da instância</b> e <b>Token de segurança da conta</b> nos campos correspondentes (mesmos nomes do painel Z-API). Pronto!
            </Step>
          </div>
        )}

        {editing && (
          <form onSubmit={save} className="mt-5 bg-slate-50 border border-slate-200 rounded-md p-4 space-y-4">
            <p className="text-[11.5px] text-slate-600 leading-relaxed -mb-1">
              Os nomes dos campos abaixo são <b>os mesmos</b> que aparecem no painel da Z-API. Copie cada valor sem cortar nenhum caractere.
            </p>

            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-medium">API da instância</span>
              <input
                value={form.instance_url || ""}
                onChange={(e) => setForm({ ...form, instance_url: e.target.value })}
                placeholder="https://api.z-api.io/instances/SEU_ID/token/SEU_TOKEN"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-slate-900"
                data-testid="zapi-form-url"
                required
              />
              <span className="text-[11px] text-slate-500 mt-1 block leading-relaxed">
                No painel Z-API: <b>Dados da instância web → API da instância</b>. Cole a linha inteira (se ela terminar em <code>/send-text</code> a gente remove sozinho).
              </span>
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-medium">Token da instância</span>
              <input
                value={form.instance_token || ""}
                onChange={(e) => setForm({ ...form, instance_token: e.target.value })}
                placeholder="exato valor da linha 'Token da instância' no painel"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-slate-900"
                data-testid="zapi-form-token"
                required
              />
              <span className="text-[11px] text-slate-500 mt-1 block leading-relaxed">
                Mesma tela <b>Dados da instância web</b>, logo abaixo da API. É o mesmo trecho que aparece dentro da URL acima depois de <code>/token/</code>.
              </span>
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-medium">Token de segurança da conta</span>
              <div className="mt-1.5 mb-2 p-2.5 bg-amber-50 border border-amber-200 rounded text-[11.5px] text-amber-900 leading-relaxed">
                <b>Atenção — este token NÃO está na tela da instância.</b><br/>
                Caminho exato no painel Z-API:<br/>
                1. Topo direito → clique no <b>seu nome</b> → <b>Minha conta</b><br/>
                <span className="text-amber-700">(ou no menu lateral esquerdo, clique em <b>Segurança</b>)</span><br/>
                2. Role até <b>"3. Token de segurança da conta"</b><br/>
                3. Copie aquele valor — é uma string aleatória diferente do ID da instância.<br/>
                Se sua conta Z-API não usa esse recurso, deixe em branco.
              </div>
              <input
                value={form.client_token || ""}
                onChange={(e) => setForm({ ...form, client_token: e.target.value })}
                placeholder="cole aqui o Token de segurança da conta"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-slate-900"
                data-testid="zapi-form-clienttoken"
              />
            </label>

            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={() => setEditing(false)} className="text-[13px] text-slate-600 hover:text-slate-900">Cancelar</button>
              <button type="submit" disabled={saving} className="rounded-md bg-slate-900 text-white px-4 py-2 text-[13px] font-medium hover:bg-slate-800 disabled:opacity-60" data-testid="zapi-form-submit">
                {saving ? "Salvando…" : "Salvar e conectar"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── STRIPE CARD ─────────────────────────────────────────────────────────────

function StripeCard({ row, onSaved }) {
  const [showSteps, setShowSteps] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(row?.config || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(row?.config || {}); }, [row]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/integrations/${row.id}`, { config: form, status: "connected" });
      toast.success("Stripe conectado!");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const connected = row?.status === "connected";

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="integration-card-stripe">
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-violet-50 text-violet-700 flex items-center justify-center">
              <CreditCard size={22} weight="fill" />
            </div>
            <div className="min-w-0">
              <h3 className="font-heading text-[15px] font-semibold text-slate-900 tracking-tight">Pagamentos</h3>
              <p className="text-[12.5px] text-slate-500 mt-0.5 leading-relaxed">
                Envia links de pagamento pelo WhatsApp e detecta confirmações automaticamente.
              </p>
            </div>
          </div>
          <StatusPill status={row?.status} />
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => setShowSteps((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-700 hover:text-slate-900 font-medium transition-colors"
            data-testid="integration-stripe-steps-toggle"
          >
            <CaretRight size={11} weight="bold" className={`transition-transform ${showSteps ? "rotate-90" : ""}`} />
            Como conectar
          </button>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-3 py-1.5 text-[12px] font-medium hover:bg-slate-800"
              data-testid="integration-stripe-configure"
            >
              {connected ? "Editar credenciais" : "Configurar agora"}
            </button>
          )}
        </div>

        {showSteps && (
          <div className="mt-5 bg-slate-50 border border-slate-200 rounded-md p-4 space-y-0.5">
            <Step n="1" title="Acesse o Stripe Dashboard">
              <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline underline-offset-2 hover:text-slate-700 inline-flex items-center gap-1">dashboard.stripe.com/apikeys <ArrowSquareOut size={10} /></a> — crie conta gratuita se ainda não tiver.
            </Step>
            <Step n="2" title="Copie a Secret Key">
              Em <b>Developers → API keys</b>, copie a <b>Secret key</b> (começa com <code>sk_live_...</code> ou <code>sk_test_...</code>).
            </Step>
            <Step n="3" title="Cole aqui no AgentUS">
              Clique em <b>"Configurar agora"</b> e cole a chave no campo abaixo.
            </Step>
          </div>
        )}

        {editing && (
          <form onSubmit={save} className="mt-5 bg-slate-50 border border-slate-200 rounded-md p-4 space-y-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-medium">Secret Key</span>
              <input
                type="password"
                value={form.api_key || ""}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="sk_live_..."
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-slate-900"
                data-testid="stripe-form-key"
                required
              />
            </label>
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={() => setEditing(false)} className="text-[13px] text-slate-600 hover:text-slate-900">Cancelar</button>
              <button type="submit" disabled={saving} className="rounded-md bg-slate-900 text-white px-4 py-2 text-[13px] font-medium hover:bg-slate-800 disabled:opacity-60">
                {saving ? "Salvando…" : "Salvar e conectar"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── PAGE ────────────────────────────────────────────────────────────────────

export default function Integrations() {
  const [rows, setRows] = useState([]);
  // v0.99.42 · Fev/2026 — Sector-aware ERP catalog.
  // `sector.code` casa com `SectorProfile.code` do backend
  // (restaurant, automotive, clinic, salon, church, etc.).
  const sector = useSector();
  const sectorCode = String(sector?.code || "").toLowerCase();
  const visibleErps = ERP_CATALOG.filter(({ allowedSectors }) => {
    if (allowedSectors === "*") return true;
    if (!Array.isArray(allowedSectors)) return false;
    return allowedSectors.map((s) => String(s).toLowerCase()).includes(sectorCode);
  });

  const load = async () => {
    const { data } = await api.get("/integrations");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const zapi = rows.find((r) => r.provider === "z_api");
  const stripe = rows.find((r) => r.provider === "stripe");

  return (
    <>
      <Topbar title="Integrações" subtitle="Conecte o WhatsApp ao AgentUS." />
      <div className="p-6 md:p-8 max-w-3xl space-y-4">
        <div className="bg-slate-900 text-white rounded-lg p-5">
          <h2 className="font-heading text-base font-semibold tracking-tight">Comece pelo WhatsApp</h2>
          <p className="text-[13px] text-slate-300 mt-1 leading-relaxed">
            Sem o WhatsApp conectado, o AgentUS não recebe mensagens de clientes. É a integração mais importante — leva 5 minutos.
          </p>
        </div>

        {zapi && <ZApiCard row={zapi} onSaved={load} />}
        {stripe && CUSTOMER_BILLING_ENABLED && <StripeCard row={stripe} onSaved={load} />}

        {/* v0.99.42 · Fev/2026 — ERPs conectáveis filtrados por setor.
            A seção só aparece quando existe ao menos 1 ERP compatível
            com o setor do tenant. Ex.: Saipos (food service) NÃO aparece
            pra oficinas, salões, clínicas, etc. */}
        {visibleErps.length > 0 && (
          <div className="pt-2" data-testid="erp-catalog-section">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2 pl-1">
              ERPs conectáveis
            </div>
            <div className="space-y-3">
              {visibleErps.map(({ key, Component }) => (
                <Component key={key} data-testid={`erp-card-${key}`} />
              ))}
            </div>
          </div>
        )}

        {!zapi && !stripe && (
          <div className="text-center text-slate-500 text-sm py-10">Carregando integrações…</div>
        )}
      </div>
    </>
  );
}
